/**
 * store/modules/flipUi.js
 *
 * Vuex module handling flip-ui state and actions.
 */
import axios from 'axios'

export default {
  // flip-ui state.
  state: {
    flipAssetList: {},
    flipCompletedAssetList: {},
    flipAuctionAssetList: {},
    flipSoldOutAssetList: {},
    flipAsset: {},
    currentInvestments: [],
    pastInvestments: [],
    consignorInvestments: [],
    purchaseAbility: {}
  },

  // Mutations to alter flip-ui state.
  mutations: {
    setFlipAssetList: (state, assetList) => (state.flipAssetList = assetList),
    setFlipCompletedAssetList: (state, assetList) => (state.flipCompletedAssetList = assetList),
    setFlipAuctionAssetList: (state, assetList) => (state.flipAuctionAssetList = assetList),
    setFlipSoldOutAssetList: (state, assetList) => (state.flipSoldOutAssetList = assetList),
    setFlipAsset: (state, asset) => (state.flipAsset = asset),
    setCurrentInvestments: (state, investments) => (state.currentInvestments = investments),
    setPastInvestments: (state, investments) => (state.pastInvestments = investments),
    setConsignorInvestments: (state, investments) => (state.consignorInvestments = investments),
    setPurchaseAbility: (state, preCheckResult) => (state.purchaseAbility = preCheckResult)
  },

  // Actions handling requests, and committing mutations.
  actions: {
    /**
     * Grab the asset list.
     *
     *  query = {
     *    sort: 'auctionDate', 'buyNowPrice', 'sharePrice'
     *    ascending: bool,
     *    offset: string,
     *    pageSize: string,
     *    promoted: string,
     *    consignorId: string,
     *    shareHolderId: string,
     *    salesState: string,
     *    visibleOnly: bool,
     *    salesModel: 'flip', 'hold'
     *  }
     *
     */
    async getFlipAssets ({ commit }, query = {}) {
      try {
        let response = await axios.get('/api/flip-ui/asset-list', {
          params: query
        })
        // Asset list fetch success, set to state.
        commit('setFlipAssetList', response.data)
      }
      catch {
        // Asset list fetch failed.
        console.error('Unable to fetch assets.')
      }
    },
    // A fetch like above just for past investments.
    async getCompletedFlipAssets ({ commit }) {
      try {
        let response = await axios.get('/api/flip-ui/asset-list', {
          params: {
            sort: 'auctionDate',
            ascending: false,
            salesState: 'Purchased',
            visibleOnly: true,
            pageSize: '100'
          }
        })
        // Asset list fetch success, set to state.
        commit('setFlipCompletedAssetList', response.data)
      }
      catch {
        // Asset list fetch failed.
        console.error('Unable to fetch completed assets.')
      }
    },
    // A fetch like above just for investments in SoldOut and auction status. (only auction for now)
    async getAuctionFlipAssets ({ commit }) {
      try {
        let response = await axios.get('/api/flip-ui/asset-list', {
          params: {
            sort: 'auctionDate',
            ascending: false,
            salesState: 'InAuction',
            visibleOnly: true,
            pageSize: '100'
          }
        })
        // Asset list fetch success, set to state.
        commit('setFlipAuctionAssetList', response.data)
      }
      catch {
        // Asset list fetch failed.
        console.error('Unable to fetch assets in Auction state.')
      }
    },

    // A fetch like above just for investments in SoldOut. (will be combined with auction eventually)
    async getSoldOutFlipAssets ({ commit }) {
      try {
        let response = await axios.get('/api/flip-ui/asset-list', {
          params: {
            sort: 'auctionDate',
            ascending: false,
            salesState: 'SoldOut',
            visibleOnly: true,
            pageSize: '100'
          }
        })
        // Asset list fetch success, set to state.
        commit('setFlipSoldOutAssetList', response.data)
      }
      catch {
        // Asset list fetch failed.
        console.error('Unable to fetch assets in SoldOut state.')
      }
    },

    // Grab asset by ID.
    async getFlipAssetById ({ commit }, assetId) {
      try {
        let response = await axios.get(`/api/flip-ui/asset-details/${assetId}`)
        // Asset list fetch success, set to state.
        commit('setFlipAsset', response.data)
      }
      catch {
        // Asset list fetch failed.
        console.error(`Unable to fetch flip asset: ${assetId}`)
      }
    },

    /**
     * GET prePurchaseCheck to determine purchase ability.
     *
     * query {
     *   numberOfSharesToPurchase: integer,
     *   userIntegrationId: integer
     * }
     */
    async prePurchaseCheck ({ commit }, { assetId, query }) {
      try {
        let response = await axios.get(`/api/flip-ui/purchase-shares/${assetId}`, {
          params: query
        })
        // Transaction success, set to state.
        commit('setPurchaseAbility', response.data)
      }
      catch (err) {
        // Asset list fetch failed.
        console.error(`Unable to fetch pre-purchase check for: ${assetId}`)
        throw(err)
      }
    },

    // Clear out the purchase ability check.
    clearPurchaseAbility ({ commit }) {
      commit('setPurchaseAbility', {})
    },

    /**
     * Purchase shares.
     *
     * payload {
     *   numberOfSharesToPurchase: integer,
     *   userIntegrationId: integer
     * }
     */
    async purchaseShares ({ commit, dispatch }, { assetId, payload }) {
      try {
        let response = await axios.post(`/api/flip-ui/purchase-shares/${assetId}`, payload)
        // Transaction success, set to state.
        commit('setTransaction', response.data)
        dispatch('getFlipAssetById', assetId)
        //dispatch('getFlipAssets')
      }
      catch (err) {
        // Asset list fetch failed.
        console.error(`Unable to purchase shares for asset: ${assetId}`)
        throw(err)
      }
    },

    /**
     * Buy it now.
     *
     * payload {
     *   userIntegrationId: integer
     * }
     */
    async buyItNow ({ commit, dispatch }, { assetId, payload }) {
      try {
        let response = await axios.post(`/api/flip-ui/buy-it-now/${assetId}`, payload)
        // Transaction success, set to state.
        commit('setTransaction', response.data)
        dispatch('getFlipAssetById', assetId)
        dispatch('getFlipAssets')
      }
      catch (err){
        // Buy asset now failed.
        console.error(`Unable to buy asset ${assetId} now.`)
        throw(err)
      }
    },

    // GET Current Investments
    async getCurrentInvestments ({ commit }) {
      try {
        let response = await axios.get('/api/flip-ui/current-investments')
        // Transaction success, set to state.
        commit('setCurrentInvestments', response.data)
      }
      catch {
        // Asset list fetch failed.
        console.error('Unable to fetch investments.')
      }
    },

    // GET Past Investments
    async getPastInvestments ({ commit }) {
      try {
        let response = await axios.get('/api/flip-ui/past-investments')
        // Transaction success, set to state.
        commit('setPastInvestments', response.data)
      }
      catch {
        // Asset list fetch failed.
        console.error('Unable to fetch past investments.')
      }
    },

    async getConsignorAssetInfo ({ commit }, consignorId) {
      try {
        let response = await axios.get(`/api/flip-ui/consignor/${consignorId}/assets`)

        // Success
        commit('setConsignorInvestments', response.data)
      }
      catch {
        // Something went wrong.
        console.error('Unable to fetch consignor asset info.')
      }
    }
  },

  // User state getters.
  getters: {
    flipAssetList: state => state.flipAssetList,
    flipCompletedAssetList: state => state.flipCompletedAssetList,
    flipAuctionAssetList: state => state.flipAuctionAssetList,
    flipSoldOutAssetList: state => state.flipSoldOutAssetList,
    flipAsset: state => state.flipAsset,
    currentInvestments: state => state.currentInvestments,
    pastInvestments: state => state.pastInvestments,
    consignorInvestments: state => state.consignorInvestments,
    purchaseAbility: state => state.purchaseAbility
  }
}
